import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { get } from 'lodash';

import { Logger } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { AccountCategory } from '@futrli-sdk/predict';

import { useRouteIds } from '../../../../hooks/useRouteIds';
import { useCoaApiView } from '../../../../chart-of-accounts/hooks/useCoaApiView';
import { useReclassifyForm } from './use-reclassify-form';
import { ReclassifyFormScreen } from './reclassify-form-screen';
import { ReclassifyLoadingScreen } from './reclassify-loading-screen';
import { ReclassifySuccessScreen } from './reclassify-success-screen';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { COADisplayAccount } from '../../../../chart-of-accounts/types';

type Screen = 'form' | 'saving' | 'saved';

export interface ReclassifyWizardContainerProps {
  onClose: () => void;
  accountToReclassify?: COADisplayAccount;
}

export const ReclassifyWizardContainer = ({ onClose, accountToReclassify }: ReclassifyWizardContainerProps) => {
  const { workspaceId, organisationId } = useRouteIds();
  const intl = useIntl();
  const { accountCategoryNames, getCategorizeError422Message } = useLocalisedConstantsContext();

  const { profitAndLossTree, balanceSheetTree, recategoriseAccount, isRecategorising } = useCoaApiView(
    workspaceId,
    organisationId
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const {
    selectedAccount,
    setSelectedAccount,
    categoryToMoveTo,
    setCategoryToMoveTo,
    accountOptions,
    categoryOptions,
    errors,
    validate,
    revalidate
  } = useReclassifyForm(profitAndLossTree, balanceSheetTree, accountToReclassify);

  useEffect(() => {
    if (isRecategorising) {
      setScreen('saving');
    }
  }, [isRecategorising]);

  const handleSave = useCallback(() => {
    if (!validate() || !selectedAccount || !categoryToMoveTo) {
      return;
    }

    setError(undefined);

    recategoriseAccount(
      {
        account_id: selectedAccount.id,
        to_category: categoryToMoveTo
      },
      {
        onSuccess: () => {
          trackEvent(TRACKING_EVENTS.RECLASSIFIED_ACCOUNTS_COA);
          setScreen('saved');
        },
        onError: e => {
          setScreen('form');
          Logger.error(e.message || ((e as unknown) as string));

          const errorStatus = getErrorStatus(e);

          if (errorStatus === ERROR_STATUS._422) {
            const originalCategoryName = accountCategoryNames[selectedAccount.category];
            const newCategoryName = accountCategoryNames[categoryToMoveTo as AccountCategory];
            const responseErrorMessageCode = get(e, 'response.data.error', '') as string;

            const errorMessageToShow = getCategorizeError422Message(
              responseErrorMessageCode,
              originalCategoryName,
              newCategoryName
            );
            setError(errorMessageToShow);
          } else {
            setError(
              intl.formatMessage({
                id: 'reporting.try_again',
                defaultMessage: 'Try again.'
              })
            );
          }
        }
      }
    );
  }, [
    validate,
    selectedAccount,
    categoryToMoveTo,
    recategoriseAccount,
    accountCategoryNames,
    getCategorizeError422Message,
    intl
  ]);

  return (
    <>
      {screen === 'form' && (
        <ReclassifyFormScreen
          onClose={onClose}
          onSave={handleSave}
          error={error}
          validationErrors={errors}
          selectedAccount={selectedAccount || undefined}
          setSelectedAccount={setSelectedAccount}
          accountOptions={accountOptions}
          categoryToMoveTo={categoryToMoveTo}
          setCategoryToMoveTo={setCategoryToMoveTo}
          categories={categoryOptions}
          revalidate={revalidate}
          hasInitialAccount={!!accountToReclassify}
        />
      )}
      {screen === 'saving' && <ReclassifyLoadingScreen />}
      {screen === 'saved' && <ReclassifySuccessScreen onClose={onClose} />}
    </>
  );
};
